import { Checkbox, Separator, Typography } from '@s4l/ds'
import React, { useMemo } from 'react'
import { usePriceDetailsHelpers } from '../hooks'
import PaymentRow from '../PaymentRow'
import RoomSection from '../RoomSection'
import styles from '../styles.module.css'
import { PAYMENT_DISPLAY_TYPES, type RoomData, type TotalData, type WalletSummary } from '../types'
import {
  calculateAggregatedTotals,
  getAllTotalsByPayment,
  getNotIncludedTaxCurrency,
  groupTaxDetails,
} from '../utils'

interface PriceDetailsContentProps {
  nights: number
  rooms: RoomData[]
  totalData?: TotalData
  wallet?: WalletSummary
  onWalletToggle?: (nextSelected: boolean) => void
}

export default function PriceDetailsContent({
  nights,
  rooms,
  totalData,
  wallet,
  onWalletToggle,
}: Readonly<PriceDetailsContentProps>) {
  const { formatCurrency, formatMessage, formatMessageCapitalized, formatDate } =
    usePriceDetailsHelpers()

  // Calculate aggregated data from rooms array
  const aggregatedData = useMemo(() => {
    if (!rooms || rooms.length === 0) {
      return null
    }
    return calculateAggregatedTotals(rooms)
  }, [rooms])

  const groupedTaxDetails = useMemo(
    () => (aggregatedData?.taxDetails ? groupTaxDetails(aggregatedData.taxDetails) : []),
    [aggregatedData?.taxDetails]
  )

  const paymentTotals = useMemo(() => getAllTotalsByPayment(groupedTaxDetails), [groupedTaxDetails])

  const payAtPropertyCurrency = useMemo(
    () =>
      aggregatedData?.taxDetails ? getNotIncludedTaxCurrency(aggregatedData.taxDetails) : undefined,
    [aggregatedData?.taxDetails]
  )

  if (!aggregatedData || !nights || nights <= 0) {
    return null
  }

  const {
    totalCurrency,
    payDate,
    hasInsurance,
    insuranceAmount,
    insuranceCurrency,
    totalWithInsurance,
    payNowWithInsurance,
    payLate,
    roomsNumber,
    isMultiRoom,
    paymentDisplayType,
  } = aggregatedData

  // Totals
  const totalPrice = totalData?.totalWithFees || totalWithInsurance
  const totalInsurance = totalData?.insurance || insuranceAmount
  const totalInsuranceCurrency = totalData?.insuranceCurrency || insuranceCurrency
  const pdpAmount = totalData?.pdp || 0
  const pdpCurrency = totalData?.pdpCurrency || totalCurrency

  // Display sections logic
  const displayInsuranceSection = hasInsurance
  const displayPdpSection = Boolean(pdpAmount > 0)
  const displayWalletSection = Boolean(
    wallet && wallet.isVisible && wallet.availableAmount && wallet.availableAmount > 0
  )
  const displayPayAtPropertySection = Boolean(paymentTotals.payLater > 0)

  // Labels and texts
  const formattedPayDate = payDate ? formatDate(payDate) : undefined
  const localCurrencyText =
    displayPayAtPropertySection && payAtPropertyCurrency
      ? `${formatMessage({ id: 'global.local-currency' })} ${formatMessage(
          { id: 'tax-breakdown.payment-at-property' },
          { currency: payAtPropertyCurrency }
        )}`
      : formatMessage({ id: 'global.local-currency' })
  const payNowLabel = formatMessage({ id: 'global.pay-now' })
  const depositSuffix =
    paymentDisplayType === PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT
      ? ` (${formatMessage({ id: 'policies.deposit' })})`
      : ''

  const handleWalletChange = () => {
    if (wallet?.loading) return

    onWalletToggle?.(!wallet?.selected)
  }

  const renderPaymentsByDate = () => (
    <>
      {aggregatedData?.paymentsByDate?.map((payment, index) => {
        const totalPayLate = totalData?.payLate ? totalData.payLate : payment.amount

        return (
          <PaymentRow
            key={payment.date || `payment-${index}`}
            label={formatMessageCapitalized(
              { id: 'policies.pay-on' },
              { date: formatDate(payment.date) }
            )}
            amount={totalPayLate}
            currency={payment.currency}
          />
        )
      })}
    </>
  )

  const renderPayNowRow = (amount: number, currency: string) => (
    <PaymentRow label={payNowLabel} amount={amount} currency={currency} />
  )

  return (
    <>
      <div className={styles.section}>
        {rooms.map((room, index) => (
          <RoomSection
            key={`room-${index + 1}`}
            room={room}
            index={index}
            nights={nights}
            isMultiRoom={isMultiRoom}
          />
        ))}

        <div className={styles.sections}>
          {displayInsuranceSection && (
            <div className={styles.insuranceRow}>
              <Typography variant="body" size="l">
                {formatMessage({ id: 'insurance.refund-protect' })}
              </Typography>
              <Typography variant="body" size="l">
                {formatCurrency(totalInsurance, totalInsuranceCurrency)}
              </Typography>
            </div>
          )}

          {displayPdpSection && (
            <div className={styles.insuranceRow}>
              <Typography variant="body" size="l">
                {formatMessage({ id: 'insurance.price-drop-protection' })}
              </Typography>
              <Typography variant="body" size="l">
                {formatCurrency(pdpAmount, pdpCurrency)}
              </Typography>
            </div>
          )}

          {displayWalletSection && (
            <div className={styles.insuranceRow}>
              <Checkbox
                name="staywallet"
                onChange={handleWalletChange}
                selected={Boolean(wallet?.selected)}
                disabled={wallet?.loading}
              >
                <Typography variant="body" size="l">
                  {`${formatMessage({ id: 'serp.filter-apply' })} ${formatMessage({ id: 'staywallet.credit' })}`}
                </Typography>
              </Checkbox>
              <Typography variant="body" size="l" bold color="var(--color-success-500)">
                {`-${formatCurrency(wallet?.availableAmount, wallet?.currency)}`}
              </Typography>
            </div>
          )}
        </div>
      </div>

      <Separator className={styles.separator} />

      <div className={styles.section}>
        <div className={styles.totalHeader}>
          <Typography variant="title" size="xs" bold>
            {formatMessage({ id: 'global.total' })}
            {totalCurrency ? ` (${totalCurrency})` : ''}
          </Typography>

          <Typography variant="title" size="xs" bold>
            {formatCurrency(totalPrice, totalCurrency)}
          </Typography>
        </div>

        <div className={styles.subHeader}>
          <Typography variant="caption" size="l">
            {`${formatMessage({ id: 'global.nights' }, { nights })}, ${formatMessage({ id: 'global.rooms-plural' }, { rooms: roomsNumber })}`}
          </Typography>
          <Typography variant="caption" size="l">
            {formatMessage({ id: 'global.taxes-fees-reduced' })}
          </Typography>
        </div>

        <div className={styles.detailsBlockDark}>
          {(() => {
            if (totalData) {
              return (
                <>
                  {!!totalData.payNow && (
                    <PaymentRow
                      label={`${payNowLabel} ${depositSuffix}`}
                      amount={totalData.payNow}
                      currency={totalCurrency}
                    />
                  )}
                  {!!totalData.payLate && (
                    <PaymentRow
                      label={formatMessageCapitalized(
                        { id: 'policies.pay-on' },
                        { date: formatDate(aggregatedData.payDate || '') }
                      )}
                      amount={totalData.payLate}
                      currency={totalCurrency}
                    />
                  )}
                </>
              )
            }

            switch (paymentDisplayType) {
              case PAYMENT_DISPLAY_TYPES.NON_REFUNDABLE:
              case PAYMENT_DISPLAY_TYPES.PAY_UPON_BOOKING_WITH_LATER_PAYMENT:
                return payNowWithInsurance > 0
                  ? renderPayNowRow(payNowWithInsurance, totalCurrency)
                  : null

              case PAYMENT_DISPLAY_TYPES.PAY_ON_ARRIVAL_ONLY:
                return (
                  <PaymentRow
                    label={formatMessageCapitalized(
                      { id: 'policies.pay-on' },
                      { date: formattedPayDate }
                    )}
                    amount={totalPrice}
                    currency={totalCurrency}
                  />
                )

              case PAYMENT_DISPLAY_TYPES.STAY_FOR_LONG_DEPOSIT:
                return (
                  <>
                    <PaymentRow
                      label={`${payNowLabel} ${depositSuffix}`}
                      amount={payNowWithInsurance}
                      currency={totalCurrency}
                    />
                    <PaymentRow
                      label={formatMessageCapitalized(
                        { id: 'policies.pay-on' },
                        { date: formattedPayDate }
                      )}
                      amount={payLate ?? undefined}
                      currency={totalCurrency}
                    />
                  </>
                )

              default:
                return (
                  <>
                    {payNowWithInsurance > 0 && renderPayNowRow(payNowWithInsurance, totalCurrency)}
                    {renderPaymentsByDate()}
                  </>
                )
            }
          })()}

          {displayPayAtPropertySection && (
            <PaymentRow
              label={formatMessageCapitalized({ id: 'tax-breakdown.pay-at-hotel' })}
              amount={paymentTotals.payLater}
              currency={paymentTotals.currency || totalCurrency}
              withTaxDetails
              taxDetails={groupedTaxDetails}
            />
          )}
        </div>
      </div>

      <div className={styles.content}>
        <Typography variant="caption" size="l" bold>
          {`${formatMessage({ id: 'global.no-conversion-fees' })}`}
        </Typography>
        <Typography variant="caption" size="l">
          {` ${localCurrencyText}`}
        </Typography>
      </div>
    </>
  )
}
